import { useCallback, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import * as Brightness from 'expo-brightness';

// Full brightness while the enlarged code is open, restoring the previous level when it closes or the pass unmounts.
const useQrPreviewBrightness = (isQrPreviewVisible) => {
	const previousBrightnessRef = useRef(null);
	const isBrightnessOverriddenRef = useRef(false);

	const setPreviewBrightness = useCallback(async () => {
		const brightnessModule = Platform.OS === 'web' ? null : Brightness;

		if (
			!brightnessModule?.setBrightnessAsync ||
			!brightnessModule?.getBrightnessAsync
		) {
			return;
		}

		try {
			if (!isBrightnessOverriddenRef.current) {
				const currentBrightness = await brightnessModule.getBrightnessAsync();
				previousBrightnessRef.current = Number.isFinite(currentBrightness)
					? currentBrightness
					: null;
			}

			await brightnessModule.setBrightnessAsync(1);
			isBrightnessOverriddenRef.current = true;
		}
		catch (error) {
			// Brightness permission availability varies per platform/device.
		}
	}, []);

	const restorePreviewBrightness = useCallback(async () => {
		if (!isBrightnessOverriddenRef.current) {
			return;
		}

		const brightnessModule = Platform.OS === 'web' ? null : Brightness;
		if (!brightnessModule?.setBrightnessAsync) {
			return;
		}

		const previousBrightness = previousBrightnessRef.current;
		isBrightnessOverriddenRef.current = false;
		previousBrightnessRef.current = null;

		if (!Number.isFinite(previousBrightness)) {
			return;
		}

		try {
			await brightnessModule.setBrightnessAsync(previousBrightness);
		}
		catch (error) {
			// Best-effort brightness restore.
		}
	}, []);

	useEffect(() => {
		if (!isQrPreviewVisible) {
			restorePreviewBrightness();
			return undefined;
		}

		setPreviewBrightness();
		return undefined;
	}, [isQrPreviewVisible, restorePreviewBrightness, setPreviewBrightness]);

	useEffect(() => {
		return () => {
			restorePreviewBrightness();
		};
	}, [restorePreviewBrightness]);
};

export default useQrPreviewBrightness;
